/**
 * Phase 16 Plan 16-03 — CancelWorkOrderDialog
 *
 * Cancels a Work Order (Any → Cancelled).
 * Requires a reason string (1-500 chars) validated via react-hook-form + zod.
 */

import type { JSX } from 'react';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useCancelWorkOrderMutation } from '@/hooks/useWorkOrderQueries';
import { useZodForm } from '@/lib/forms/useZodForm';
import { toast } from '@/lib/toast';
import type { WorkOrderDetail } from '@/types/work-orders';

const cancelSchema = z.object({
  reason: z
    .string()
    .min(1, 'Reason is required')
    .max(500, 'Reason must be at most 500 characters'),
});

export interface CancelWorkOrderDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (o: boolean) => void;
  readonly workOrder: WorkOrderDetail;
}

export function CancelWorkOrderDialog({
  open,
  onOpenChange,
  workOrder,
}: CancelWorkOrderDialogProps): JSX.Element {
  const form = useZodForm({
    schema: cancelSchema,
    defaultValues: { reason: '' },
  });

  const mutation = useCancelWorkOrderMutation();

  function handleClose(): void {
    form.reset();
    mutation.reset();
    onOpenChange(false);
  }

  const onSubmit = form.handleSubmit((values) => {
    mutation.mutate(
      { id: workOrder.id, body: { reason: values.reason } },
      {
        onSuccess: () => {
          toast.success(`Work Order ${workOrder.woNumber} cancelled`);
          form.reset();
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error('Cancel failed', { description: err.detail ?? err.message });
        },
      },
    );
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent data-testid="cancel-wo-dialog">
        <DialogHeader>
          <DialogTitle>Cancel Work Order</DialogTitle>
          <DialogDescription>
            Cancel <strong>{workOrder.woNumber}</strong>. This action cannot be undone.
            Please provide a reason for cancellation.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} noValidate>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="cancel-wo-reason">Reason * (max 500 characters)</Label>
              <Textarea
                id="cancel-wo-reason"
                placeholder="Describe why this Work Order is being cancelled…"
                rows={4}
                data-testid="cancel-wo-dialog-reason"
                {...form.register('reason')}
              />
              {form.formState.errors.reason !== undefined && (
                <p className="text-xs text-destructive" data-testid="cancel-wo-dialog-reason-error">
                  {form.formState.errors.reason.message}
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={mutation.isPending}
              data-testid="cancel-wo-dialog-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={mutation.isPending}
              data-testid="cancel-wo-dialog-submit"
            >
              {mutation.isPending ? 'Cancelling…' : 'Cancel Work Order'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
